// Pinterest de la persona: conectar, saber si está conectado, desconectar y traer sus tableros y
// pines. Los tokens nunca pasan por acá: los guarda y los usa atlas-backend
// (ADR-2026-10-23-pinterest-guardados-como-referencia).
//
// Flujo de conexión, igual que Mercado Libre:
//   1. click → `abrirVentanaDePinterest()` (SINCRÓNICO, antes de cualquier await)
//   2. `conectarPinterestEnVentana(popup)`: POST /v1/pinterest/conectar → URL de Pinterest → la
//      persona acepta → el backend deja la conexión pendiente → pinterest-conectado.html avisa el
//      reclamo → POST /v1/pinterest/reclamar la activa con la sesión de la MISMA persona.

import { atlasBackendFetch, atlasBackendGet } from './atlasBackend';
import {
  CLAVE_AVISO_PINTEREST,
  codigoDeError,
  ErrorDePinterest,
  ESPERA_MAXIMA_MS,
  estadoDesdeRespuesta,
  EVENTO_CAMBIO_DE_PINTEREST,
  leerAvisoDePinterest,
  pinesDesdeRespuesta,
  tablerosDesdeRespuesta,
  type AvisoDePinterest,
  type EstadoDePinterest,
  type PinDePinterest,
  type TableroDePinterest,
} from './pinterestConexionLogica';

export {
  ErrorDePinterest,
  EVENTO_CAMBIO_DE_PINTEREST,
  mensajeDeErrorDePinterest,
  type EstadoDePinterest,
  type PinDePinterest,
  type TableroDePinterest,
} from './pinterestConexionLogica';

function avisarCambio() {
  try {
    window.dispatchEvent(new Event(EVENTO_CAMBIO_DE_PINTEREST));
  } catch {
    /* noop */
  }
}

/**
 * Abre la ventana del login. **Llamar SIEMPRE de forma sincrónica dentro del handler del click**,
 * nunca después de un `await`: Safari y todos los navegadores de iPhone la bloquean
 * (ADR-2026-08-07-tiendanube-popup-safari).
 */
/** La persona tocó "Cancelar" en Atlas mientras conectaba: no es un error que haya que mostrar. */
export const CANCELADO_EN_ATLAS = 'cancelado_en_atlas';

/**
 * Atlas instalado en el iPhone (pantalla de inicio): la ventana de Pinterest se abre en otro
 * navegador, sin `opener` y con otro localStorage, así que el aviso nunca vuelve. Mismo chequeo
 * que `soportePush` (lib/push.ts).
 */
export function esAppInstaladaEnIOS(): boolean {
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return isIOS && standalone;
}

export function abrirVentanaDePinterest(): Window | null {
  return window.open('/pinterest-conectando.html', 'pinterest_connect', 'width=620,height=760');
}

async function leerJson(r: Response): Promise<unknown> {
  try {
    return await r.json();
  } catch {
    return null;
  }
}

function errorDeRed(e: unknown): ErrorDePinterest {
  const sinSesion = e instanceof Error && /sesión/i.test(e.message);
  return new ErrorDePinterest(sinSesion ? 'sin_sesion' : 'red');
}

async function post<T>(ruta: string, body: Record<string, unknown>): Promise<T> {
  let r: Response;
  try {
    r = await atlasBackendFetch(ruta, body);
  } catch (e) {
    throw errorDeRed(e);
  }
  const cuerpo = await leerJson(r);
  if (!r.ok) throw new ErrorDePinterest(codigoDeError(r.status, cuerpo));
  return cuerpo as T;
}

async function get(ruta: string): Promise<unknown> {
  let r: Response;
  try {
    r = await atlasBackendGet(ruta);
  } catch (e) {
    throw errorDeRed(e);
  }
  const cuerpo = await leerJson(r);
  if (!r.ok) throw new ErrorDePinterest(codigoDeError(r.status, cuerpo));
  return cuerpo;
}

function cerrar(popup: Window | null | undefined) {
  try {
    popup?.close();
  } catch {
    /* noop */
  }
}

function olvidarAviso() {
  try {
    localStorage.removeItem(CLAVE_AVISO_PINTEREST);
  } catch {
    /* noop */
  }
}

/**
 * Espera el aviso de `pinterest-conectado.html`: por postMessage (lo normal) o por localStorage
 * (si el navegador perdió el `opener`). Rechaza si cierran la ventana antes o si se agota el tiempo.
 */
function esperarAviso(popup: Window, signal?: AbortSignal): Promise<AvisoDePinterest> {
  return new Promise((resolve, reject) => {
    let listo = false;
    const terminar = (aviso: AvisoDePinterest | null, err?: ErrorDePinterest) => {
      if (listo) return;
      listo = true;
      window.removeEventListener('message', alMensaje);
      window.removeEventListener('storage', alStorage);
      signal?.removeEventListener('abort', alCancelar);
      clearInterval(vigia);
      clearTimeout(limite);
      olvidarAviso();
      if (aviso) resolve(aviso);
      else reject(err);
    };
    const delStorage = () => {
      try {
        return leerAvisoDePinterest(localStorage.getItem(CLAVE_AVISO_PINTEREST));
      } catch {
        return null;
      }
    };
    const alMensaje = (e: MessageEvent) => {
      // Sólo la página de nuestro propio origen: el reclamo no lo manda nadie más.
      if (e.origin !== window.location.origin) return;
      const aviso = leerAvisoDePinterest(e.data);
      if (aviso) terminar(aviso);
    };
    const alStorage = (e: StorageEvent) => {
      if (e.key !== CLAVE_AVISO_PINTEREST) return;
      const aviso = leerAvisoDePinterest(e.newValue);
      if (aviso) terminar(aviso);
    };
    // "Cancelar" en Atlas: se cierra la ventana y se deja de esperar, sin mensaje de error.
    const alCancelar = () => {
      cerrar(popup);
      terminar(null, new ErrorDePinterest(CANCELADO_EN_ATLAS));
    };
    window.addEventListener('message', alMensaje);
    window.addEventListener('storage', alStorage);
    signal?.addEventListener('abort', alCancelar);
    // Si cierran la ventana, puede que el aviso ya esté en localStorage.
    const vigia = setInterval(() => {
      if (!popup.closed) return;
      const aviso = delStorage();
      terminar(aviso, aviso ? undefined : new ErrorDePinterest('ventana_cerrada'));
    }, 700);
    const limite = setTimeout(() => {
      cerrar(popup);
      terminar(null, new ErrorDePinterest('tiempo'));
    }, ESPERA_MAXIMA_MS);
  });
}

/**
 * Corta la espera de `promesa` si la persona toca "Cancelar": cierra la ventana en el acto, aunque el
 * pedido al backend siga en vuelo (review Greptile #207). Lo que llegue después se ignora.
 */
function hastaQueCancele<T>(promesa: Promise<T>, popup: Window, signal?: AbortSignal): Promise<T> {
  if (!signal) return promesa;
  return new Promise<T>((resolve, reject) => {
    const alCancelar = () => {
      cerrar(popup);
      reject(new ErrorDePinterest(CANCELADO_EN_ATLAS));
    };
    if (signal.aborted) {
      alCancelar();
      return;
    }
    signal.addEventListener('abort', alCancelar, { once: true });
    promesa.then(resolve, reject).finally(() => signal.removeEventListener('abort', alCancelar));
  });
}

/**
 * Conecta el Pinterest de la persona sobre una ventana **ya abierta por el click**
 * (`abrirVentanaDePinterest`). Tira `ErrorDePinterest` con un mensaje listo para mostrar.
 */
export async function conectarPinterestEnVentana(
  popup: Window | null | undefined,
  signal?: AbortSignal,
): Promise<{ username: string | null }> {
  if (!popup) throw new ErrorDePinterest('popup_bloqueado');
  olvidarAviso(); // que no se cuele el aviso de un intento anterior
  let url: string;
  try {
    ({ url } = await hastaQueCancele(post<{ url: string }>('/v1/pinterest/conectar', {}), popup, signal));
  } catch (e) {
    cerrar(popup);
    throw e;
  }
  // La URL la arma el backend, pero igual: sólo se navega a la autorización de Pinterest.
  if (!/^https:\/\/(www\.)?pinterest\.com\/oauth\//.test(url)) {
    cerrar(popup);
    throw new ErrorDePinterest('error');
  }
  if (popup.closed) throw new ErrorDePinterest('ventana_cerrada');
  popup.location.href = url;

  const aviso = await esperarAviso(popup, signal);
  cerrar(popup);
  if ('error' in aviso) throw new ErrorDePinterest(aviso.error);
  const r = await post<{ username?: string | null }>('/v1/pinterest/reclamar', { reclamo: aviso.reclamo });
  avisarCambio();
  return { username: r?.username ?? null };
}

/** Estado de la conexión. Liviano: el backend sólo lee su base. Un 404 = no disponible. */
export async function estadoDePinterest(): Promise<EstadoDePinterest> {
  try {
    const r = await atlasBackendGet('/v1/pinterest/estado');
    return estadoDesdeRespuesta(r.status, await leerJson(r));
  } catch {
    return estadoDesdeRespuesta(0, null);
  }
}

/** Desconecta el Pinterest de la persona (el backend borra la conexión). */
export async function desconectarPinterest(): Promise<void> {
  await post('/v1/pinterest/desconectar', {});
  avisarCambio();
}

/** Los tableros, pedidos a Pinterest en el momento. */
export async function tablerosDePinterest(siguiente?: string | null): Promise<{ tableros: TableroDePinterest[]; siguiente: string | null }> {
  const q = siguiente ? `?siguiente=${encodeURIComponent(siguiente)}` : '';
  return tablerosDesdeRespuesta(await get(`/v1/pinterest/tableros${q}`));
}

/** Los pines de un tablero, o todos los guardados (sin tablero), pedidos en el momento. */
export async function pinesDePinterest(p: { tableroId?: string | null; siguiente?: string | null }): Promise<{
  pines: PinDePinterest[];
  siguiente: string | null;
}> {
  const q = new URLSearchParams();
  if (p.tableroId) q.set('tablero_id', p.tableroId);
  if (p.siguiente) q.set('siguiente', p.siguiente);
  const s = q.toString();
  return pinesDesdeRespuesta(await get(`/v1/pinterest/pines${s ? `?${s}` : ''}`));
}
